import { Component } from '@angular/core'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { RowOrigin } from '../../models/source-key.model'

/** One source that will be detached, shown for information only. */
export interface UnmatchSourceOption {
  origin: RowOrigin
  label: string
  color: string
  count: number
}

/**
 * Resolution of the confirm.
 *
 * There is no per-source choice any more: partial unmatch has no backend primitive
 * (`FlexUnmatchCommand` carries only a `matchId`, and `RemoveMatchGroupMembersCommand` is explicitly
 * not wired — PVAL-26291 Scope Out), so an unmatch always dissolves the whole group.
 */
export interface UnmatchModalResult {
  confirmed: true
}

/**
 * Confirm before dissolving a match group.
 *
 * This used to be a source *picker* — "detach one source, or the whole group" — backed by
 * {@link MatchingService.unmatchOrigin}. That affordance is gone rather than reworded, because the
 * choice it offered cannot be honoured: the flex endpoint dissolves whole groups only. What remains
 * is an informational confirm listing which sources are about to be detached, which is worth keeping:
 * the user can see a multi-source group on screen and would reasonably expect to be asked before all
 * of it comes apart.
 *
 * {@link UnmatchSourceOption} and the component itself are retained under the same names because
 * `libs/panels/positions-2` carries a deliberate copy of this layer.
 */
@Component({
  selector: 'hs-unmatch-modal',
  templateUrl: './unmatch-modal.component.html',
  styleUrls: ['./unmatch-modal.component.scss'],
})
export class UnmatchModalComponent {
  title = ''
  sources: UnmatchSourceOption[] = []

  /**
   * How many distinct match groups the selection spans, as counted by
   * {@link MatchingService.matchGroupKeysOf}.
   *
   * Drives the singular/plural copy. It is a FLOOR, not an exact figure — a group member outside the
   * loaded page cannot be seen from the client — hence "at least" in the plural wording. Defaults to
   * 1 so a caller that does not set it gets the single-group sentence rather than a bare "0".
   */
  groupCount = 1

  /** True once the selection provably spans more than one group, i.e. the plural copy applies. */
  get multipleGroups(): boolean {
    return this.groupCount > 1
  }

  constructor(readonly activeModal: NgbActiveModal) {}

  confirm(): void {
    this.activeModal.close({ confirmed: true } as UnmatchModalResult)
  }
}
